import type { ReactNode } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import { type Combatant, type EncounterState, OVERSHIELD_RESOURCE } from "@lancer-vtt/domain";
import type { LoadedOverlay } from "@lancer-vtt/rules";
import type { UseEncounter } from "../useEncounter";
import { setTrackCommand } from "../tracks";
import type { ActionDraft } from "../combatInspector";
import { TurnControls } from "./TurnControls";
import { UnitSheet, UnitSheetStatuses, type UnitSheetIdentity } from "./UnitSheet";
import { SkirmishChooser } from "./SkirmishChooser";
import { PreparedHoldPanel } from "./inspectorPanels";
import { UnitActionColumn } from "./unitActions";
import { useUnitActionModel } from "./useUnitActionModel";
import { resourcePools, UnitSheetVitals } from "./unitVitals";
import { UnitLoadout } from "./UnitLoadout";
import { mechLoadout } from "../unitLoadout";
import { gmReloadRowActions } from "./gmReload";
import { descriptiveStatCells, StatStrip, HaseStatRow } from "./unitStats";
// Type-only — the REAL exported contract, so a change to DICE.app's entry point breaks this
// file's compile rather than drifting past it.
import type { PreselectBonus } from "../../../routes/home/DiceApp";

/**
 * The **MECH** PLAY inspector body — the mech's sheet, routed from {@link InspectorBody} when
 * `combatant.kind === "mech"`. It fills the {@link UnitSheet} shell (header, state line, banners)
 * and its shared sections: the vitals ({@link UnitSheetVitals}: HP and HEAT, STRUCT and STRESS
 * pips, REPAIR), the stat grid and HASE row, the statuses, and the loadout ({@link UnitLoadout}:
 * weapons in mount order, systems, then core, talents and traits, each row's rule text a click away).
 * The GM's tools are the GM strip at the sheet's foot (`UnitGmStrip`), which {@link InspectorBody}
 * passes in as `gm`.
 *
 * **Actions are not here.** The token command menu (`TokenCommandMenu.tsx`, docked in the unit
 * column above this sheet's panel) is the mech's one action surface: the sheet's QUICK / FULL grid and its above-grid
 * choosers were retired in `token-command-menu` Phase 4. The sheet keeps what the menu does not
 * host: the **REACTION** column (the loadout's REACTIONS group), the prepared-hold panel ({@link PreparedHoldPanel}: RELEASE NOW /
 * DROP for every client), END TURN, and the vitals and stats. One chooser stays: a Skirmish armed with a fixed target (the Overwatch take, the
 * advisor's plan) shows {@link SkirmishChooser} with its FIRE button, since neither comes from the
 * menu. Both renderers read the one action model ({@link useUnitActionModel}).
 */
export function MechInspectorBody({
  combatant: c,
  identity,
  state,
  actorId,
  encounter,
  aim,
  setAim,
  fireAt,
  onDeclineOverwatch,
  canControl,
  gm,
  error,
  compendium,
  overlay,
  loadingOverlay,
  onOpenDice,
}: {
  combatant: Combatant;
  /** The sheet header's identity, derived once by {@link InspectorBody}. */
  identity: UnitSheetIdentity;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
  aim: ActionDraft | null;
  setAim: (a: ActionDraft | null) => void;
  /** Fire the armed action at a target id — the host's `fireAt`, for the fixed-target {@link SkirmishChooser}'s FIRE button. */
  fireAt: (targetId: string) => void;
  /** The Overwatch take's explicit decline (the {@link SkirmishChooser}'s ×): the only local gesture that answers the checkpoint on the server. */
  onDeclineOverwatch?: () => void;
  canControl: boolean;
  /** The GM strip (`UnitGmStrip`), built by {@link InspectorBody} for the GM's sheet only. */
  gm?: ReactNode;
  error: string | null;
  compendium: Compendium;
  /** The merged action overlay a granted row's `resolvable` is derived against. */
  overlay: LoadedOverlay;
  /** True until {@link overlay}'s sidecar fetch has settled — see {@link CompendiumState.loadingOverlay}. */
  loadingOverlay: boolean;
  /**
   * Open the DICE rail page pre-filled — the host's `useCombatDice()` (`combatApp/CombatDice.tsx`).
   * Threaded down to the stat strip + HASE row, where each
   * rollable cell grows a roll icon-button (step 3.2).
   *
   * **Required-nullable, mirroring `PilotBuildSheet`'s own `onOpenDice`:** `null` is a real,
   * explicit choice meaning "this mount has no DICE to open" (a render test, a future host
   * without one) and renders zero roll buttons. Making every call site pick is the point — an
   * optional prop would let a new host forget and silently ship an un-rollable sheet.
   */
  onOpenDice: ((preselect: PreselectBonus) => void) | null;
}) {
  const setTrack = (kind: "hp" | "heat", value: number) =>
    encounter.dispatch(setTrackCommand({ actorId, combatantId: c.id, kind, value }));

  // The descriptive mini-stats a player eyeballs, straight off the frozen base — built by the shared
  // `descriptiveStatCells` so the three PLAY bodies cannot drift on which fields the strip carries or
  // on how an absent one renders. `grit: true`: a mech's `base.attackBonus` IS its pilot's GRIT
  // (`spawn/mech.ts`). The old fifth cell labelled "SAVE" is gone, not renamed in place: it rendered
  // `base.save`, the defender's roll BONUS, under a label a reader takes for the save TARGET — the
  // strip now shows `base.saveTarget`, the real target, and no fallback (tracker D5).
  const stats = descriptiveStatCells(c.base, { grit: true });

  // The two mech consequence tracks as pips (A3) — STRUCTURE and STRESS. Default to the frame-universal
  // 4 for any blob predating the track, and treat a missing `current` as full.
  const maxStruct = c.base.maxStructure ?? 4;
  const curStruct = c.current.structure ?? maxStruct;
  const maxStress = c.base.maxStress ?? 4;
  const curStress = c.current.stress ?? maxStress;
  const exposed = c.statuses.some((s) => s.name.toLowerCase() === "exposed");

  // OVERSHIELD — display-only, absent = 0 (a chip on the pips row, {@link UnitSheetVitals}).
  const shield = c.current.resources?.[OVERSHIELD_RESOURCE] ?? 0;

  // The whole action model — descriptors, the pre-activation economy, the row views, the status
  // block reasons and every handler — lives in {@link useUnitActionModel}, shared with the token
  // command menu. This body only lays it out.
  const {
    isActiveActor,
    reactOpen,
    reactAvail,
    endTurn,
    reactionActions,
    grantedReactions,
    fireGranted,
    statusBlockReason,
    isArmed,
    endAttack,
    repairsLeft,
    held,
    releaseArmed,
    armRelease,
    dropHeld,
  } = useUnitActionModel({ combatant: c, state, actorId, isGm: encounter.isGm, canControl, compendium, overlay, loadingOverlay, encounter, aim, setAim });

  return (
    <UnitSheet
      identity={identity}
      gm={gm}
      className="unit-sheet-control"
      stateLine={
        // The state line, END TURN (a pass on a READY unit), and the GM's START TURN on a player's unit
        // (`TurnControls`). There is no ACTIVATE TURN: any other action on a ready unit starts its
        // turn through the pre-activation fold (`token-command-menu` 2c).
        <TurnControls combatantId={c.id} state={state} actorId={actorId} encounter={encounter} canControl={canControl} isActiveActor={isActiveActor} onEndTurn={endTurn} noun="mech" />
      }
      error={error}
      hold={
        held && (
          <PreparedHoldPanel
            held={held}
            weapons={c.base.weapons ?? []}
            releaseArmed={releaseArmed}
            canControl={canControl}
            onArmRelease={armRelease}
            onDrop={dropHeld}
          />
        )
      }
    >
      <UnitSheetVitals
        hp={c.current.hp}
        maxHp={c.base.maxHp}
        heat={c.base.maxHeat !== undefined ? { value: c.current.heat ?? 0, max: c.base.maxHeat } : undefined}
        exposed={exposed}
        structure={{ value: curStruct, max: maxStruct }}
        stress={{ value: curStress, max: maxStress }}
        repair={c.base.repairCapMax !== undefined ? { value: repairsLeft, max: c.base.repairCapMax } : undefined}
        shield={shield}
        burn={c.current.burn ?? 0}
        pools={resourcePools(c.current.resources)}
        onSetTrack={canControl ? setTrack : null}
        disabled={!actorId}
      />

      <StatStrip cells={stats} onOpenDice={onOpenDice} />

      {/* HASE — the four pilot skills (tracker D4). `spawnMech` freezes the pilot's real
          `mechSkills` for HULL/AGILITY/SYSTEMS; ENGINEERING is not frozen yet and shows as absent
          until step 2.3 lands — and, being absent, carries no roll button either. See
          {@link HaseStatRow}. */}
      <HaseStatRow base={c.base} onOpenDice={onOpenDice} />

      <UnitSheetStatuses combatant={c} edit={encounter.isGm ? { state, actorId, dispatch: encounter.dispatch } : null} />

      {/* The Overwatch take (`CombatApp`'s `startOverwatchTake`) and the GM advisor both arm a
          Skirmish with a fixed target, and neither comes from the command menu, so their chooser
          (weapon pick + FIRE at the pinned target) stays on the sheet. Every other action is the
          token command menu's (`token-command-menu` Phase 4). */}
      {canControl && isArmed && aim !== null && aim.mode === "skirmish" && (aim.reaction === true || aim.fixedTargetId !== undefined) && (
        <div className="unit-sheet-armed-wrap">
          <SkirmishChooser combatant={c} state={state} aim={aim} setAim={setAim} onEndAttack={endAttack} onFire={fireAt} onDeclineOverwatch={onDeclineOverwatch} />
        </div>
      )}
      {/* The loadout (4.2), read-only, with the REACTION column beside it: Overwatch and Brace stay
          clickable on the sheet until the reactions-in-menu follow-up (Decision 2), and so do granted
          reactions taken by hand (Reel In, Issue Command). */}
      <UnitLoadout
        groups={mechLoadout({ combatant: c, state, compendium })}
        rowActions={gmReloadRowActions({ combatant: c, actorId, isGm: encounter.isGm, dispatch: encounter.dispatch })}
        reactions={
          <UnitActionColumn
            title="REACTION"
            tone="react"
            actions={[...reactionActions, ...grantedReactions]}
            combatantId={c.id}
            available={reactOpen}
            availLabel={reactAvail}
            canControl={canControl}
            onGrantedActivate={fireGranted}
            statusBlockReason={statusBlockReason}
          />
        }
      />
    </UnitSheet>
  );
}
